<template>
  <article
    class="agent-action"
    :class="`agent-action--${status.tone}`"
    :aria-label="`${kind.label} ${recipientName ? `to ${recipientName}` : ''}`"
  >
    <header class="agent-action__header">
      <span class="agent-action__icon"><Icon :icon="kind.icon" /></span>
      <div class="agent-action__heading">
        <p class="agent-action__title">{{ title ?? kind.label }}</p>
        <p v-if="action.contact" class="agent-action__recipient">
          <span>to</span>
          <RouterLink
            :to="{ name: CONTACT_OVERVIEW_ROUTE, params: { id: action.contact.id } }"
            class="agent-action__contact"
          >
            {{ recipientName }}
          </RouterLink>
          <span v-if="recipientDetail" class="agent-action__muted">· {{ recipientDetail }}</span>
        </p>
        <p v-else-if="contactCount" class="agent-action__recipient">
          {{ contactCount }} {{ contactCount === 1 ? "contact" : "contacts" }}
        </p>
      </div>
      <span class="agent-action__status" role="status">
        <Spinner v-if="status.busy" size="xs" />
        <Icon v-else :icon="status.icon" />
        {{ status.label }}
      </span>
    </header>

    <!-- What will go out, or the rep's edit of it. -->
    <div v-if="editing" class="agent-action__edit">
      <template v-if="action.actionType === 'send_email'">
        <Input v-model="draft.subject" size="sm" label="Subject" />
        <div class="agent-action__field">
          <span class="agent-action__label">Message</span>
          <RichTextEditor v-model="draft.body" height="200px" :toolbar="EMAIL_TOOLBAR" />
        </div>
      </template>
      <Textarea
        v-else-if="action.actionType === 'send_linkedin_message'"
        v-model="draft.text"
        label="Message"
        :rows="6"
        :max-length="8000"
      />
      <Textarea
        v-else-if="action.actionType === 'send_linkedin_invite'"
        v-model="draft.note"
        label="Invitation note (optional)"
        :rows="3"
        :max-length="INVITE_NOTE_MAX"
        show-char-count
      />
    </div>

    <div v-else class="agent-action__body">
      <template v-if="action.actionType === 'send_email'">
        <p class="agent-action__subject">{{ input.subject }}</p>
        <div
          class="agent-action__message agent-action__message--html"
          :class="{ 'agent-action__message--clamped': isLong && !expanded }"
          v-html="emailHtml"
        />
      </template>
      <p
        v-else-if="action.actionType === 'send_linkedin_message'"
        class="agent-action__message"
        :class="{ 'agent-action__message--clamped': isLong && !expanded }"
      >
        {{ input.text }}
      </p>
      <template v-else-if="action.actionType === 'send_linkedin_invite'">
        <p v-if="input.note" class="agent-action__message">{{ input.note }}</p>
        <p v-else class="agent-action__muted">No note: a plain connection request.</p>
      </template>
      <dl v-else-if="action.actionType === 'book_meeting'" class="agent-action__facts">
        <dt>Title</dt>
        <dd>{{ input.title }}</dd>
        <dt>When</dt>
        <dd>{{ meetingTime }}</dd>
        <dt>Length</dt>
        <dd>{{ input.durationMinutes ?? 30 }} minutes</dd>
        <template v-if="input.provider || input.meetingUrl">
          <dt>Where</dt>
          <dd>{{ input.provider ? providerLabel(input.provider) : input.meetingUrl }}</dd>
        </template>
      </dl>
      <button
        v-if="isLong"
        type="button"
        class="agent-action__more"
        :aria-expanded="expanded"
        @click="expanded = !expanded"
      >
        {{ expanded ? "Show less" : "Show more" }}
      </button>
    </div>

    <p v-if="action.reason" class="agent-action__reason">
      <Icon icon="mdi:lightbulb-on-outline" />
      <span>{{ action.reason }}</span>
    </p>
    <p v-if="outcome" class="agent-action__outcome">{{ outcome }}</p>

    <form v-if="rejecting" class="agent-action__reject" @submit.prevent="submitReject">
      <Input
        v-model="rejectReason"
        size="sm"
        placeholder="Tell the assistant why (optional)"
        aria-label="Why you're rejecting this"
      />
      <div class="agent-action__buttons">
        <Button variant="ghost" size="sm" :disabled="busy" @click="rejecting = false">
          Cancel
        </Button>
        <Button variant="danger" size="sm" type="submit" :loading="busy">Reject</Button>
      </div>
    </form>

    <footer v-else-if="isOpen" class="agent-action__footer">
      <span class="agent-action__expiry">{{ expiryText }}</span>
      <div v-if="editing" class="agent-action__buttons">
        <Button variant="ghost" size="sm" :disabled="busy" @click="cancelEdit">Cancel</Button>
        <Button
          variant="primary"
          size="sm"
          :loading="busy"
          :disabled="!isDraftValid"
          @click="saveAndApprove"
        >
          Save &amp; {{ kind.verb }}
        </Button>
      </div>
      <div v-else class="agent-action__buttons">
        <Button
          variant="ghost"
          size="sm"
          :disabled="busy"
          @click="quickReject ? emit('reject') : (rejecting = true)"
        >
          {{ rejectLabel ?? "Reject" }}
        </Button>
        <slot name="actions" :busy="busy" />
        <Button v-if="isEditable" variant="outline" size="sm" :disabled="busy" @click="startEdit">
          Edit
        </Button>
        <Button variant="primary" size="sm" :loading="busy" @click="emit('approve')">
          {{ approveLabel ?? `Approve & ${kind.verb}` }}
        </Button>
      </div>
    </footer>
  </article>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { RouterLink } from "vue-router";
import DOMPurify from "dompurify";
import { Button, Icon, Input, RichTextEditor, Spinner, Textarea } from "@/components/ui";
import { CONTACT_OVERVIEW_ROUTE } from "@/router/routes";
import type { AgentAction, AgentActionType } from "@/dtos/Agent.dto";

const props = defineProps<{
  action: AgentAction;
  /** A decision on this action is in flight. */
  busy?: boolean;
  /** The current time, ticked by the parent, so the card can tell it has expired. */
  now: number;
  /** Replaces the action's own label, e.g. "AI suggested reply". */
  title?: string;
  approveLabel?: string;
  rejectLabel?: string;
  /** Rejects in one click, without asking the rep why. */
  quickReject?: boolean;
}>();

const emit = defineEmits<{
  /** `payload` is the rep's edit: the whole input in the action's own shape. */
  approve: [payload?: Record<string, unknown>];
  reject: [reason?: string];
}>();

// Mirrors the backend tool's limit (send_linkedin_invite note).
const INVITE_NOTE_MAX = 200;
// Longer drafts start collapsed.
const LONG_TEXT_CHARS = 500;
const EMAIL_TOOLBAR = [
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link"],
  ["clean"],
];

const KINDS: Record<AgentActionType, { label: string; icon: string; verb: string; done: string }> =
  {
    send_email: { label: "Email", icon: "mdi:email-outline", verb: "send", done: "Sent" },
    send_linkedin_message: {
      label: "LinkedIn message",
      icon: "mdi:linkedin",
      verb: "send",
      done: "Sent",
    },
    send_linkedin_invite: {
      label: "LinkedIn invitation",
      icon: "mdi:account-plus-outline",
      verb: "send",
      done: "Invite sent",
    },
    book_meeting: {
      label: "Meeting",
      icon: "mdi:calendar-clock-outline",
      verb: "book",
      done: "Booked",
    },
    enroll_sequence: {
      label: "Enroll in sequence",
      icon: "mdi:playlist-play",
      verb: "enroll",
      done: "Enrolled",
    },
    create_campaign: {
      label: "Campaign",
      icon: "mdi:rocket-launch-outline",
      verb: "create",
      done: "Created",
    },
    bulk_update: {
      label: "Bulk update",
      icon: "mdi:pencil-box-multiple-outline",
      verb: "apply",
      done: "Applied",
    },
    spend_credits: {
      label: "Import prospects",
      icon: "mdi:database-import-outline",
      verb: "import",
      done: "Imported",
    },
  };

const kind = computed(
  () =>
    KINDS[props.action.actionType] ?? {
      label: "Action",
      icon: "mdi:robot-outline",
      verb: "run",
      done: "Done",
    }
);

/** What will actually go out: the rep's edit when there is one. */
const input = computed<Record<string, any>>(
  () => props.action.editedPayload ?? props.action.payload ?? {}
);

const recipientName = computed(() => {
  const c = props.action.contact;
  return c ? [c.fname, c.lname].filter(Boolean).join(" ") || c.email || "Unnamed contact" : "";
});

const recipientDetail = computed(() => {
  const c = props.action.contact;
  if (!c) return "";
  return props.action.actionType === "send_email" ? (c.email ?? "") : (c.jobTitle ?? "");
});

const contactCount = computed(() =>
  Array.isArray(input.value.contactIds) ? input.value.contactIds.length : 0
);

// Pending past its expiry: the server sweeps it within a minute, the card stops it now.
const isLapsed = computed(
  () =>
    props.action.status === "pending" &&
    !!props.action.expiresAt &&
    new Date(props.action.expiresAt).getTime() <= props.now
);
const isOpen = computed(() => props.action.status === "pending" && !isLapsed.value);

const status = computed(() => {
  if (isLapsed.value)
    return { tone: "muted", icon: "mdi:timer-off-outline", label: "Expired", busy: false };
  switch (props.action.status) {
    case "pending":
      return props.busy
        ? { tone: "busy", icon: "", label: "Working…", busy: true }
        : {
            tone: "pending",
            icon: "mdi:hand-back-right-outline",
            label: "Needs approval",
            busy: false,
          };
    case "approved":
    case "executing":
      return { tone: "busy", icon: "", label: "Sending…", busy: true };
    case "executed":
      return { tone: "success", icon: "mdi:check-circle", label: kind.value.done, busy: false };
    case "failed":
      return { tone: "danger", icon: "mdi:alert-circle", label: "Failed", busy: false };
    case "rejected":
      return { tone: "muted", icon: "mdi:close-circle-outline", label: "Rejected", busy: false };
    case "expired":
      return { tone: "muted", icon: "mdi:timer-off-outline", label: "Expired", busy: false };
    case "superseded":
      return { tone: "muted", icon: "mdi:skip-next-circle-outline", label: "Skipped", busy: false };
    default:
      return {
        tone: "muted",
        icon: "mdi:help-circle-outline",
        label: props.action.status,
        busy: false,
      };
  }
});

// Why it ended the way it did, in the server's words.
const outcome = computed(() => {
  const result = props.action.executionResult;
  switch (props.action.status) {
    case "failed":
      return result?.error || "It couldn't be sent.";
    case "superseded":
      return result?.reason || "Not sent: the contact replied or opted out first.";
    case "rejected":
      return props.action.rejectionReason ? `You said: ${props.action.rejectionReason}` : "";
    default:
      return "";
  }
});

const expiryText = computed(() => {
  if (!props.action.expiresAt) return "";
  const minutes = Math.round((new Date(props.action.expiresAt).getTime() - props.now) / 60_000);
  if (minutes < 60) return `Expires in ${Math.max(minutes, 1)} min`;
  const hours = Math.round(minutes / 60);
  return hours < 48 ? `Expires in ${hours}h` : `Expires in ${Math.round(hours / 24)} days`;
});

const emailHtml = computed(() => DOMPurify.sanitize(String(input.value.body ?? "")));

const plainText = (html: string): string => {
  const el = document.createElement("div");
  el.innerHTML = DOMPurify.sanitize(html);
  return (el.textContent ?? "").trim();
};

const isLong = computed(() => {
  if (props.action.actionType === "send_email") {
    return plainText(String(input.value.body ?? "")).length > LONG_TEXT_CHARS;
  }
  if (props.action.actionType === "send_linkedin_message") {
    return String(input.value.text ?? "").length > LONG_TEXT_CHARS;
  }
  return false;
});
const expanded = ref(false);

const meetingTime = computed(() => {
  const start = input.value.startTime ? new Date(input.value.startTime) : null;
  if (!start || Number.isNaN(start.getTime())) return input.value.startTime ?? "";
  return start.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
});

const providerLabel = (provider: string): string =>
  ({ zoom: "Zoom", google: "Google Meet", teams: "Microsoft Teams" })[provider.toLowerCase()] ??
  provider;

// Only the words of a message are editable; who, when and which sequence stay as proposed.
const isEditable = computed(() =>
  ["send_email", "send_linkedin_message", "send_linkedin_invite"].includes(props.action.actionType)
);

const editing = ref(false);
const draft = reactive({ subject: "", body: "", text: "", note: "" });

const startEdit = () => {
  draft.subject = String(input.value.subject ?? "");
  draft.body = String(input.value.body ?? "");
  draft.text = String(input.value.text ?? "");
  draft.note = String(input.value.note ?? "");
  editing.value = true;
};

const cancelEdit = () => (editing.value = false);

const isDraftValid = computed(() => {
  switch (props.action.actionType) {
    case "send_email":
      return !!draft.subject.trim() && !!plainText(draft.body);
    case "send_linkedin_message":
      return !!draft.text.trim();
    case "send_linkedin_invite":
      return draft.note.length <= INVITE_NOTE_MAX;
    default:
      return true;
  }
});

const editedPayload = (): Record<string, unknown> => {
  const base = { ...input.value };
  switch (props.action.actionType) {
    case "send_email":
      return { ...base, subject: draft.subject.trim(), body: draft.body };
    case "send_linkedin_message":
      return { ...base, text: draft.text.trim() };
    case "send_linkedin_invite": {
      const note = draft.note.trim();
      if (note) return { ...base, note };
      delete base.note;
      return base;
    }
    default:
      return base;
  }
};

const saveAndApprove = () => {
  if (!isDraftValid.value) return;
  emit("approve", editedPayload());
};

const rejecting = ref(false);
const rejectReason = ref("");

const submitReject = () => {
  emit("reject", rejectReason.value.trim() || undefined);
};

// Once decided (here, in bulk or by the server), the edit and reject forms have nothing to do.
watch(isOpen, (open) => {
  if (!open) {
    editing.value = false;
    rejecting.value = false;
  }
});
</script>

<style scoped>
.agent-action {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 640px;
  padding: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  font-size: 14px;
}

.agent-action--pending {
  border-color: #c4b5fd;
  box-shadow: 0 1px 2px rgb(124 58 237 / 8%);
}

.agent-action__header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.agent-action__icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f5f3ff;
  color: #7c3aed;
  font-size: 18px;
}

.agent-action__heading {
  flex: 1;
  min-width: 0;
}

.agent-action__title {
  margin: 0;
  color: #111827;
  font-weight: 600;
}

.agent-action__recipient {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 2px 0 0;
  color: #6b7280;
  font-size: 13px;
}

.agent-action__contact {
  color: #7c3aed;
  font-weight: 500;
  text-decoration: none;
}

.agent-action__contact:hover {
  text-decoration: underline;
}

.agent-action__muted {
  margin: 0;
  color: #6b7280;
  font-size: 13px;
}

.agent-action__status {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.agent-action--pending .agent-action__status {
  background: #fef3c7;
  color: #92400e;
}

.agent-action--busy .agent-action__status {
  background: #ede9fe;
  color: #6d28d9;
}

.agent-action--success .agent-action__status {
  background: #dcfce7;
  color: #166534;
}

.agent-action--danger .agent-action__status {
  background: #fee2e2;
  color: #b91c1c;
}

.agent-action__body {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f9fafb;
}

.agent-action__subject {
  margin: 0;
  color: #111827;
  font-weight: 600;
}

.agent-action__message {
  margin: 0;
  color: #374151;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.agent-action__message--html {
  white-space: normal;
}

.agent-action__message--html :deep(p) {
  margin: 0 0 6px;
}

.agent-action__message--clamped {
  max-height: 140px;
  overflow: hidden;
  -webkit-mask-image: linear-gradient(to bottom, #000 70%, transparent);
  mask-image: linear-gradient(to bottom, #000 70%, transparent);
}

.agent-action__more {
  align-self: flex-start;
  padding: 0;
  border: none;
  background: none;
  color: #7c3aed;
  font-size: 13px;
  cursor: pointer;
}

.agent-action__facts {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px 12px;
  margin: 0;
}

.agent-action__facts dt {
  color: #6b7280;
}

.agent-action__facts dd {
  margin: 0;
  color: #111827;
}

.agent-action__edit {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.agent-action__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.agent-action__label {
  color: #374151;
  font-size: 13px;
  font-weight: 500;
}

.agent-action__reason {
  display: flex;
  gap: 6px;
  margin: 0;
  color: #6b7280;
  font-size: 13px;
}

.agent-action__reason svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.agent-action__outcome {
  margin: 0;
  color: #4b5563;
  font-size: 13px;
}

.agent-action--danger .agent-action__outcome {
  color: #b91c1c;
}

.agent-action__footer,
.agent-action__reject {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.agent-action__reject > :first-child {
  flex: 1;
  min-width: 200px;
}

.agent-action__expiry {
  color: #9ca3af;
  font-size: 12px;
}

.agent-action__buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
}
</style>
